import { useRef, useCallback, useState, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { ActivationTrigger } from '../types.ts';
import { ActivationReason, ActivationTriggerType } from '../types.ts';

import type { GateType } from './Gate.tsx';

type UseActivationTriggersParams = {
	triggers?: ActivationTrigger[];
	gate: GateType | null;
	activateComponent: (reason: ActivationReason) => void;
	boundaryId: string;
	analyticsName?: string;
	analyticsSource?: 'activatableComponent' | 'loadable';
	immediateActivationCondition?: boolean;
};

export function useActivationTriggers({
	triggers,
	gate,
	activateComponent,
	boundaryId,
	analyticsName,
	analyticsSource = 'activatableComponent',
}: UseActivationTriggersParams): (element: HTMLDivElement | null) => void {
	const observerRef = useRef<IntersectionObserver | null>(null);
	const activatedByScrollRef = useRef(false);
	const [isInViewport, setIsInViewport] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const triggerCleanupRef = useRef<(() => void) | null>(null);
	const autoActivateTimeoutRef = useRef<number | null>(null);
	const hostRef = useRef<HTMLDivElement | null>(null);

	const createTriggerHandlers = useCallback(
		(element: HTMLDivElement, triggerList: ActivationTrigger[]) => {
			const removers: Array<() => void> = [];
			triggerList.forEach((trigger) => {
				if (trigger.type === ActivationTriggerType.HOVER) {
					const handleHover = () => {
						activateComponent(ActivationReason.USER_ACTIVATION);
					};

					element.addEventListener('mouseenter', handleHover, { passive: true });
					removers.push(() => {
						element.removeEventListener('mouseenter', handleHover);
					});
				} else if (trigger.type === ActivationTriggerType.SCROLL_INTO_VIEW) {
					if (expVal('confluence_frontend_loadable_sync_activation', 'isEnabled', false)) {
						if (element && !activatedByScrollRef.current) {
							if (!observerRef.current) {
								observerRef.current = new IntersectionObserver(([entry]) => {
									if (entry.isIntersecting) {
										activatedByScrollRef.current = true;
										observerRef.current?.disconnect();
										activateComponent(ActivationReason.USER_ACTIVATION);
									}
								});
							}
							observerRef.current.observe(element);
							removers.push(() => {
								observerRef.current?.disconnect();
							});
						}
					} else {
						if (element && !isInViewport) {
							if (!observerRef.current) {
								observerRef.current = new IntersectionObserver(([entry]) => {
									if (entry.isIntersecting) {
										setIsInViewport(true);
										observerRef.current?.disconnect();
									}
								});
							}
							observerRef.current.observe(element);
							removers.push(() => {
								observerRef.current?.disconnect();
							});
						}
					}
				}
			});
			return () => {
				removers.forEach((off) => off());
			};
		},
		[activateComponent, isInViewport],
	);

	const hostRefWithActivation = useCallback(
		(element: HTMLDivElement | null) => {
			hostRef.current = element;

			if (element) {
				// When the experiment is enabled, trigger setup is deferred to a useEffect
				// to avoid hydration errors
				if (
					triggers &&
					triggers.length &&
					!expVal('confluence_frontend_loadable_defer_triggers', 'isEnabled', false)
				) {
					triggerCleanupRef.current = createTriggerHandlers(element, triggers);
				}
			}
		},
		[createTriggerHandlers, triggers],
	);

	// When the experiment is enabled, set up trigger handlers in a useEffect instead
	// of the ref callback. This ensures triggers don't fire until after hydration
	useEffect(() => {
		const element = hostRef.current;
		if (
			!expVal('confluence_frontend_loadable_defer_triggers', 'isEnabled', false) ||
			!element ||
			!triggers?.length
		) {
			return;
		}

		triggerCleanupRef.current = createTriggerHandlers(element, triggers);

		return () => {
			triggerCleanupRef.current?.();
			triggerCleanupRef.current = null;
		};
	}, [createTriggerHandlers, triggers]);

	useEffect(() => {
		if (
			!expVal('confluence_frontend_loadable_sync_activation', 'isEnabled', false) &&
			isInViewport
		) {
			activateComponent(ActivationReason.USER_ACTIVATION);
		}
	}, [isInViewport, activateComponent]);

	useEffect(() => {
		// Don't auto-activate if only SSR_ONLY trigger is present
		const hasOnlySSROnly =
			triggers?.length === 1 && triggers[0].type === ActivationTriggerType.SSR_ONLY;

		const ms = StatsigConfigurations.getNumber('confluence_loadable_auto_activate_timeout_ms', 0);
		if (ms && !hasOnlySSROnly) {
			autoActivateTimeoutRef.current = window.setTimeout(() => {
				if (gate?.getStatus() === 'suspended') {
					activateComponent(ActivationReason.AUTO_TIMEOUT);
				}
			}, ms);
		}

		return () => {
			clearTimeout(autoActivateTimeoutRef.current ?? undefined);
			triggerCleanupRef.current?.();

			const shouldSendAnalytics =
				gate?.getStatus() === 'suspended' &&
				triggers &&
				triggers.length &&
				(analyticsSource === 'loadable' || analyticsName);

			if (shouldSendAnalytics) {
				const attributes =
					analyticsSource === 'loadable'
						? {
								loadableName: analyticsName,
								wasEverActivated: false,
								placeholderId: boundaryId,
							}
						: {
								analyticsName,
								wasEverActivated: false,
								boundaryId,
							};

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: analyticsSource,
						action: 'unmounted',
						actionSubject:
							analyticsSource === 'loadable' ? 'loadableComponent' : 'activatableComponent',
						attributes,
					},
				}).fire();
			}
		};
	}, [
		activateComponent,
		gate,
		createAnalyticsEvent,
		boundaryId,
		analyticsName,
		triggers,
		analyticsSource,
	]);

	return hostRefWithActivation;
}
